/**
 * How a TestIt invitation is worded where the citizen ACCEPTS it — the web
 * review dialog and accept page, the phone's review sheet. (D3 · 4 Oct 2026)
 *
 * A TestIt invitation is accepted as TESTS. It used to be worded as a journey
 * decision — "Join {{company}}", "Which journey do you share?", "will see your
 * CheckUp" (false: a TestIt relationship is born without that scope) — and it
 * named no test. Every sentence here is about the tests, and what it says the
 * company will see is the SERVER's answer (`sharesCheckup`, from the consent
 * the relationship holds once accepted), never a guess from the journey.
 *
 * Each key is a literal, so the key-usage gate checks every one.
 */
type Translate = (key: string, options?: Record<string, unknown>) => string;

/** The review's title — an ask for results, never "Join". */
export function testitAcceptTitle(t: Translate, company: string): string {
  return t('invitations.testit.title', { company });
}

/** What is asked: tests still to take, or — all of them already done — nothing to take. */
export function testitAcceptBody(
  t: Translate,
  input: { company: string; completed: boolean }
): string {
  return input.completed
    ? t('invitations.testit.bodyAllDone', { company: input.company })
    : t('invitations.testit.body', { company: input.company });
}

/**
 * What accepting shares. A relationship born of a TestIt invitation holds the
 * tests' results and nothing else; one the company already holds keeps what it
 * holds, which may include the CheckUp — said as it is, not denied.
 */
export function testitAcceptScope(
  t: Translate,
  input: { company: string; sharesCheckup: boolean }
): string {
  return input.sharesCheckup
    ? t('invitations.testit.scopeUnchanged', { company: input.company })
    : t('invitations.testit.scopeTestsOnly', { company: input.company });
}

/** One asked test's status: done — its result's date — or still to take. */
export function testitAskedTestStatus(
  t: Translate,
  test: { takenAt: string | null },
  formatDate: (iso: string) => string
): string {
  return test.takenAt
    ? t('invitations.testit.testDone', { date: formatDate(test.takenAt) })
    : t('invitations.testit.testPending');
}

/** The accept button: sharing what exists, or accepting tests still to take. */
export function testitAcceptLabel(t: Translate, completed: boolean): string {
  return completed ? t('invitations.testit.acceptShare') : t('invitations.testit.accept');
}

/** The confirmation of an ask that was complete the moment it was accepted. */
export function testitAcceptConfirmation(
  t: Translate,
  company: string
): { title: string; body: string } {
  return {
    title: t('invitations.testit.confirmTitle', { company }),
    body: t('invitations.testit.confirmBody', { company }),
  };
}
